import 'leaflet/dist/leaflet.css';
import './leaflet.override.css';
import 'leaflet/dist/leaflet.js';
import { Skeleton } from '@volue/wave-react';
import type { Map } from 'leaflet';
import { latLng, DomEvent, Icon } from 'leaflet';
import leafletMarker2x from 'leaflet/dist/images/marker-icon-2x.png';
import leafletMarker from 'leaflet/dist/images/marker-icon.png';
import leafletShadow from 'leaflet/dist/images/marker-shadow.png';
import React, { useState } from 'react';
import { MapContainer } from 'react-leaflet';

import type { MapConfiguration } from '@/common/overmind/namespaces/arcgis/config/config.types';
import matchState from '@/common/utils/match';
import { useAppState } from '@/overmind';

import styles from './LeafletMap.module.css';
import { SaveMapPosition } from './SaveMapPosition';
import { ZoomController } from './ZoomController';
import { useBaseMapLayer, useBasemapProjection } from './useBaseMapLayer';
import { useMapSizeInvalidation } from './useMapSizeInvalidation';

// Fix default images when using local files for leaflet
Icon.Default.mergeOptions({
  iconRetinaUrl: leafletMarker2x,
  iconUrl: leafletMarker,
  shadowUrl: leafletShadow
});

type LeafletMapProps = React.PropsWithChildren<{
  invalidateMapTrigger?: unknown;
}>;

const mapLoading = () => <Skeleton height="100%" />;

export function LeafletMap({
  children,
  invalidateMapTrigger
}: LeafletMapProps) {
  const configState = useAppState().arcgis.config;
  const mapState = useAppState().map;
  const [map, setMap] = useState<Map | null>(null);

  const disableEventPropagation = (element: HTMLDivElement) => {
    if (element) {
      DomEvent.disableClickPropagation(element);
      DomEvent.disableScrollPropagation(element);
    }
  };
  useBaseMapLayer(map);
  const projection = useBasemapProjection();

  React.useEffect(() => {
    console.log('projection', projection);
  }, [projection]);
  useMapSizeInvalidation(map, invalidateMapTrigger);

  const mapComponent = (config: MapConfiguration) => {
    if (!projection) return null;
    const { crs, transform } = projection;
    const center =
      mapState.center ?? transform([config.map.centerX, config.map.centerY]);

    return (
      <MapContainer
        crs={crs}
        ref={setMap}
        center={center}
        zoom={mapState.zoom ?? config.map.zoom}
        minZoom={config.map.minZoom}
        maxZoom={25}
        scrollWheelZoom
        className={styles.mainMap}
        zoomControl={false}
      >
        <div ref={disableEventPropagation} className="epsilon">
          {children}
        </div>
        <SaveMapPosition />
        <ZoomController />
      </MapContainer>
    );
  };

  return matchState(configState, {
    NOT_LOADED: () => mapLoading(),
    LOADING: () => mapLoading(),
    LOADED: ({ value }) => mapComponent(value),
    ERROR: ({ error }) => <div className="pAl red100">{error}</div>
  });
}
